import React from "react";
import { get } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { getSignedInOrgUnit, isCurrentUserLoading } from "app/auth/store/reducers/user.reducer";
import { AccessDeniedState } from "components/items/empty-state";
import LoadingState from "components/items/loading-state";
import withPermissions from "permissions/withPermissions";
import { isSystemConfigurationLoaded } from "app/auth/store/reducers/system-configuration";
import { setUpReferralWorklistFilter } from "app/main/referralWorklist/actions/referralWorklist.actions";
import { areFiltersLoaded } from "app/main/referralWorklist/reducers/referralWorklist.reducers";
import { fetchAllCodeSetValues } from "app/main/codeSet/actions/codeSet.actions";

import ReferralWorklistPage from "./referral-worklist-page";
import { changeSignedInOrgUnit } from "../../../auth/store/actions";

/*
  * This component is quite similar to the SecureReferralWorklistPage component.
  * The difference is that this component receives the orgUnitId from the URL params
  * and changes to the site that orgUnitId refers to, instead of getting it from the signedInOrgUnit.
 */
const ThroughLinkReferralWorklistPage = ({
  location,
  match: { params: { orgUnitId } },
  hasPermissionReferralsView,
  hasPermissionChangeCurrentSiteConfiguration,
}) => {
  const signedInOrgUnit = useSelector(getSignedInOrgUnit);
  const dispatch = useDispatch();
  const loading = useSelector(isCurrentUserLoading);
  const isSystemConfigLoaded = useSelector(isSystemConfigurationLoaded);
  const filtersLoaded = useSelector(areFiltersLoaded);
  const forceLoad = get(location, ["state", "forceLoad"], false);
  const [isLoadingSettings, setLoadingSettings] = React.useState(true);
  const [updatingOrgUnitId, setUpdatingOrgUnitId] = React.useState(false);
  const orgSiteNeedsChange = signedInOrgUnit?.id !== orgUnitId;

  React.useEffect(() => {
    if (isSystemConfigLoaded && !filtersLoaded) {
      dispatch(setUpReferralWorklistFilter());
      setLoadingSettings(false);
      // load all codeSet values for display and used in selectors
      dispatch(fetchAllCodeSetValues());
    } else if (filtersLoaded) {
      setLoadingSettings(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSystemConfigLoaded]);

  React.useEffect(() => {
    // if the configurations are loaded and the user signed in orgUnit is different from the orgUnitId in the URL
    // then we should change the site automatically
    const shouldChangeSite = () => isSystemConfigLoaded
        && !isLoadingSettings
        && orgSiteNeedsChange
        && hasPermissionChangeCurrentSiteConfiguration;

    const changeSite = () => {
      setUpdatingOrgUnitId(true);
      dispatch(changeSignedInOrgUnit(orgUnitId));
      // no need to set the updatingOrgUnitId to false here because the component will be unmounted
      // otherwise we will have the error:  "Can't perform a React state update on an unmounted component"
    };

    if (shouldChangeSite()) {
      changeSite();
    }

    return () => {
      setUpdatingOrgUnitId(false);
    };
  }, [
    dispatch,
    isSystemConfigLoaded,
    isLoadingSettings,
    signedInOrgUnit,
    orgUnitId,
    hasPermissionChangeCurrentSiteConfiguration,
    orgSiteNeedsChange]);

  if (loading || isLoadingSettings || updatingOrgUnitId) return <LoadingState />;

  if (!hasPermissionReferralsView || (orgSiteNeedsChange && !hasPermissionChangeCurrentSiteConfiguration)) return <AccessDeniedState />;

  return <ReferralWorklistPage forceLoad={forceLoad} orgUnitId={orgUnitId} />;
};

export default withPermissions("ReferralsView", "ChangeCurrentSiteConfiguration")(ThroughLinkReferralWorklistPage);
